<template>
  <div
    class="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
  >
    <!-- User Header -->
    <div
      class="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
      @click="open = !open"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <!-- Expand/Collapse Button -->
          <button
            type="button"
            class="w-8 h-8 rounded-full btn-icon btn-icon-default flex items-center justify-center transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" :class="[{'rotate-90': open}]"
              class="h-4 w-4 transition-transform duration-200">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
          </button>

          <!-- User Avatar -->
          <div class="from-blue-400 to-blue-600 w-10 h-10 bg-gradient-to-br rounded-full flex items-center justify-center">
            <span class="text-white font-semibold text-sm">
              {{ user.name.charAt(0).toUpperCase() }}
            </span>
          </div>

          <!-- User Info -->
          <div class="flex-1">
            <div class="flex items-center space-x-3">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ user.name }}
              </h3>
            </div>

            <p class="text-sm text-gray-600">
              {{ user.email }}
            </p>
          </div>
        </div>

        <!-- Last Login & Project Count -->
        <div class="text-right">
          <div class="flex flex-col items-end justify-end space-y-1">
            <!-- Project Count Badge -->
            <div class="flex items-center space-x-2">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

              <span class="text-sm font-medium text-gray-900">
                {{ user.projects.length }} project{{ user.projects.length !== 1 ? 's' : '' }}
              </span>
            </div>

            <!-- Last Login -->
            <div class="text-sm text-gray-500">
              {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.lastLogin') }} {{ user.last_login_at ? DateTime.fromISO(user.last_login_at).toLocaleString(DateTime.DATETIME_SHORT) : $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.never') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expanded Content -->
    <div
      v-if="open"
      class="border-t border-gray-200 bg-gray-50"
      :class="{ 'opacity-70 cursor-not-allowed pointer-events-none': detachRoleAction.is(States.LOADING) || attachRoleAction.is(States.LOADING) }"
      @click.stop=""
    >
      <div class="p-4 space-y-4">
        <!-- Assign Project Section -->
        <div
          v-if="companyProjectsWithNoRole.length > 0"
          class="bg-white rounded-lg border border-gray-200 p-4"
        >
          <div class="flex items-center space-x-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <h4 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.assignProject') }}
            </h4>
          </div>

          <form
            class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end"
            @submit.prevent="attachRole"
          >
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.project') }}
              </label>

              <AppSelect
                v-model="userForm.form.project_id"
                class="form-input"
                name="project_id"
                :options="companyProjectsWithNoRole"
                :error="userForm.getErrors('project_id')"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.role') }}
              </label>

              <AppSelect
                v-model="userForm.form.role_id"
                class="form-input"
                name="role_id"
                :options="roles"
                :error="userForm.getErrors('role_id')"
              />
            </div>

            <button
              type="submit"
              class="btn btn--primary-blue flex items-center justify-center space-x-2"
              @click.prevent="attachRole"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <span>{{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.addProject') }}</span>
            </button>
          </form>
        </div>

        <!-- Current Projects Section -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-center space-x-2 mb-4">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

            <h4 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.projects') }}
            </h4>

            <span class="bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded-full">
              {{ user.projects.length }}
            </span>
          </div>

          <div
            v-if="user.projects.length === 0"
            class="text-center py-8"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="mx-auto h-12 w-12 text-gray-400 mb-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

            <p class="text-gray-500 text-sm">
              No projects assigned yet
            </p>
          </div>

          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="project in user.projects"
              :key="project.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
            >
              <div class="flex items-center space-x-3">
                <div>
                  <p class="font-medium text-gray-900">
                    {{ project.name }}
                  </p>

                  <p class="text-sm text-gray-600">
                    {{ $t('ProjectSettings.CompanyUsersTab.CompanyUserRow.as') }} {{ roleNames.get(project.pivot.role_id).descriptor }}
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="group w-8 h-8 rounded-full btn-icon btn-icon-red btn-icon--bg-red flex items-center justify-center transition-colors duration-200"
                @click="detachRole(project)"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Company from '@/models/Company';
import Project from '@/models/Project';
import User from '@/models/User';
import { useProjectsStore } from '@/store/projects';
import { useRolesStore } from '@/store/roles';
import { useUserStore } from '@/store/user';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';

const props = defineProps<{
  user: User;
  company: Company;
}>();

const open = ref(false);
const projectsStore = useProjectsStore();

const companyProjectsWithNoRole = computed(() => {
  return projectsStore.models.query().where('company_id', props.company.id).whereDoesntHave('users', (query) => {
    query.where('id', props.user.id);
  }).get().map((project) => {
    return {
      label: project.name,
      value: project.id,
    };
  });
});

const rolesStore = useRolesStore();

const roles = computed(() => {
  return rolesStore.models.where('name', (name: string) => !['admin', 'company_admin'].includes(name)).get().map(
    (role) => {
      return {
        label: t(`Models.Role.descriptors.${role.name}`),
        value: role.id,
      };
    },
  );
});

const userForm = useFormData({
  user_id: props.user.id,
  role_id: undefined,
  project_id: undefined,
});

const userStore = useUserStore();
const attachRoleAction = useStoreApiAction(userStore.attachCompanyUserRole);

const attachRole = () => {
  userForm.resetErrors();

  attachRoleAction.request(props.company.id, userForm.form.project_id, userForm.form).then((data) => {
    userForm.resetData(['project_id', 'role_id']);
  }).catch((error) => {
    userForm.setErrors(error.data);
  });
};

const detachRoleAction = useStoreApiAction(userStore.detachCompanyUserRole);

const detachRole = (project: Project) => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.CompanyUsersTab.CompanyUserRow.removeUserTitle'),
    text: t('ProjectSettings.CompanyUsersTab.CompanyUserRow.removeUserText', {
      userName: props.user.name,
      projectName: project.name,
    }),

    showConfirmButton: true,
    confirmButtonText: t('ProjectSettings.CompanyUsersTab.CompanyUserRow.yes'),
    confirmButtonColor: 'green',
    showDenyButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      detachRoleAction.request(props.company.id, project.id, userForm.form).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('ProjectSettings.CompanyUsersTab.CompanyUserRow.failedToDetach'),
        });
      });
    }
  });
};

const roleNames = computed(() => {
  return new Map(rolesStore.models.all().map((role) => [role.id, role]));
});
</script>

<style scoped></style>
